import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSetupProgress } from "../api/setupApi";
import { useToast } from "./useToast";

/**
 * Custom hook for handling setup flow navigation
 * Provides smart navigation logic for setup steps vs regular usage
 */

/**
 * Calculates optimistic progress state after a step is completed
 * @param {Object} currentProgress - Current setup progress data
 * @param {string} completedStep - The step that was just completed
 * @returns {Object} Updated progress data
 */
const calculateOptimisticProgress = (currentProgress, completedStep) => {
  const optimisticProgress = {
    ...currentProgress,
    availabilityCompleted:
      completedStep === "availability"
        ? true
        : currentProgress.availabilityCompleted,
    serviceCompleted:
      completedStep === "service" ? true : currentProgress.serviceCompleted,
    bookingLinkCompleted:
      completedStep === "booking-page"
        ? true
        : currentProgress.bookingLinkCompleted,
  };

  // Calculate if setup is now complete
  optimisticProgress.isSetupComplete =
    optimisticProgress.availabilityCompleted &&
    optimisticProgress.serviceCompleted &&
    optimisticProgress.bookingLinkCompleted;

  return optimisticProgress;
};

/**
 * Determines the next incomplete setup step route
 * @param {Object} progressData - Setup progress data
 * @returns {string|null} Next step route or null if all complete
 */
export const getNextStepRoute = (progressData) => {
  if (!progressData.availabilityCompleted) return "/availability";
  if (!progressData.serviceCompleted) return "/services";
  if (!progressData.bookingLinkCompleted) return "/booking-page/general";
  return null; // All steps complete
};

/**
 * Gets the next step information including message and button text
 * @param {Object} progressData - Setup progress data
 * @returns {Object|null} Step info with message and buttonText or null
 */
export const getNextStepInfo = (progressData) => {
  if (!progressData.availabilityCompleted)
    return {
      message: "Ready to set up your availability?",
      buttonText: "Set Up Availability",
    };
  if (!progressData.serviceCompleted)
    return {
      message: "Ready to create your services?",
      buttonText: "Create Services",
    };
  if (!progressData.bookingLinkCompleted)
    return {
      message: "Ready to set up your booking page?",
      buttonText: "Set Up Booking Page",
    };
  return null; // All steps complete
};

/**
 * Gets the next step name for display
 * @param {Object} progressData - Setup progress data
 * @returns {string|null} Step name or null
 */
export const getNextStepName = (progressData) => {
  const stepInfo = getNextStepInfo(progressData);
  return stepInfo ? stepInfo.buttonText : null;
};

export const useSetupNavigation = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  // Get current setup progress
  const { data: setupProgressResponse, isLoading: isSetupLoading } = useQuery({
    queryKey: ["setupProgress"],
    queryFn: getSetupProgress,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: false,
    retry: 3,
  });

  const setupProgress = setupProgressResponse?.data;
  const isSetupComplete = setupProgress?.isSetupComplete || false;

  /**
   * Determines the next incomplete setup step
   * @param {Object} progressData - Optional progress data to check (defaults to current setupProgress)
   * @returns {string|null} Next step name or null if all complete
   */
  const getNextIncompleteStep = (progressData = setupProgress) => {
    if (!progressData) return null;

    if (!progressData.availabilityCompleted) return "availability";
    if (!progressData.serviceCompleted) return "services";
    if (!progressData.bookingLinkCompleted) return "booking-page";

    return null; // All steps complete
  };

  /**
   * Handles post-completion feedback for setup steps.
   * Returns next step info when a step is newly completed, or null otherwise.
   * @param {string} stepType - The type of step completed ('availability', 'service', 'booking-page')
   * @param {string} successMessage - Message to show on success
   * @returns {{ route: string, stepName: string } | null}
   */
  const handleSetupStepComplete = async (
    stepType,
    successMessage = "Updated successfully"
  ) => {
    try {
      // Get current setup progress from React Query cache
      const currentData = queryClient.getQueryData(["setupProgress"]);
      const currentProgress = currentData?.data;

      let nextStep = null;

      if (currentProgress) {
        // Optimistically calculate new progress state
        const optimisticProgress = calculateOptimisticProgress(
          currentProgress,
          stepType
        );

        if (optimisticProgress.isSetupComplete) {
          toast.success("Setup complete!", "Your booking system is ready.");
        } else {
          toast.success(successMessage);

          // Check if this call newly completed the step
          const stepWasAlreadyDone =
            (stepType === "availability" && currentProgress.availabilityCompleted) ||
            (stepType === "service" && currentProgress.serviceCompleted) ||
            (stepType === "booking-page" && currentProgress.bookingLinkCompleted);

          if (!stepWasAlreadyDone) {
            const nextRoute = getNextStepRoute(optimisticProgress);
            const nextStepInfo = getNextStepInfo(optimisticProgress);
            if (nextRoute && nextStepInfo) {
              nextStep = { route: nextRoute, stepName: nextStepInfo.buttonText };
            }
          }
        }
      } else {
        toast.success(successMessage);
      }

      // Background refresh of setup progress
      queryClient.invalidateQueries({ queryKey: ["setupProgress"], exact: true });
      return nextStep;
    } catch (error) {
      console.error("Setup progress handling failed:", error);
      toast.success(successMessage);
      return null;
    }
  };

  return {
    handleSetupStepComplete,
    isSetupComplete,
    setupProgress,
    isSetupLoading,
    getNextIncompleteStep,
  };
};
